// Догрузка «застрявших» изображений.
//
// Проблема, которую решаем: после клиентской навигации (SPA) часть картинок с
// нативным `loading="lazy"` так и не начинает грузиться — элемент в макете есть,
// но пустой, и пользователь видит «дырки» до F5.
//
// ВАЖНО: раньше здесь на всякий случай делался сброс `img.src = ''` — это обрывало
// уже начавшуюся загрузку на медленном канале и само порождало битые картинки.
// Теперь логика такая:
//   * снимаем `loading="lazy"` — если загрузка ещё не стартовала, браузер начнёт её сразу;
//   * если картинка уже грузится (complete === false) — НЕ ТРОГАЕМ, пусть догружается;
//   * если картинка «завершена» без данных (complete && naturalWidth === 0) — один раз
//     перезапрашиваем с cache-bust `?retry=…` (лечит 404 старого ассета и битый кэш).
const FORCE_TRIED = 'forceLoadTried';
const RETRY_TRIED = 'retryTried';

const retryImage = (img) => {
  if (!img.dataset || img.dataset[RETRY_TRIED]) {
    return;
  }
  const url = img.currentSrc || img.src;
  if (!url || url.indexOf('data:') === 0) {
    return;
  }
  img.dataset[RETRY_TRIED] = '1';
  const clean = url.split('?')[0];
  img.src = clean + '?retry=' + Date.now();
};

const forceLoad = (img) => {
  if (!img || img.tagName !== 'IMG' || !img.dataset) {
    return;
  }
  if (img.dataset[FORCE_TRIED]) {
    return;
  }
  img.dataset[FORCE_TRIED] = '1';

  // снимаем нативную ленивость: если загрузка не началась — начнётся сейчас
  img.removeAttribute('loading');

  if (img.complete && img.naturalWidth === 0) {
    // «завершена», но данных нет — это и есть битая картинка
    retryImage(img);
  }
};

const hasSize = (img) => {
  const rect = img.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
};

// range — насколько далеко от вьюпорта ещё трогаем (в высотах экрана)
const forceNear = (range = 1.5) => {
  const margin = window.innerHeight * range;

  document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    if (img.dataset && img.dataset[FORCE_TRIED]) {
      return;
    }
    if (!hasSize(img)) {
      return;
    }
    const rect = img.getBoundingClientRect();
    if (rect.bottom > -margin && rect.top < window.innerHeight + margin) {
      forceLoad(img);
    }
  });
};

// добиваем то, что осталось битым в зоне видимости
const retryBrokenVisible = () => {
  const margin = window.innerHeight * 2;

  document.querySelectorAll('img').forEach((img) => {
    if (!img.complete || img.naturalWidth > 0) {
      return;
    }
    if (!img.src || img.src.indexOf('data:') === 0) {
      return;
    }
    if (!hasSize(img)) {
      return;
    }
    const rect = img.getBoundingClientRect();
    if (rect.bottom > -margin && rect.top < window.innerHeight + margin) {
      retryImage(img);
    }
  });
};

export default function () {
  if (process.server || typeof window === 'undefined') {
    return;
  }

  let observer = null;

  const observe = (img) => {
    if (!img || img.tagName !== 'IMG') {
      return;
    }
    if (img.dataset && img.dataset[FORCE_TRIED]) {
      return;
    }
    if (!img.hasAttribute('loading')) {
      return;
    }

    if (observer) {
      observer.observe(img);
    }
  };

  const startObserver = () => {
    if (!('IntersectionObserver' in window)) {
      return;
    }

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        const img = entry.target;
        observer.unobserve(img);
        forceLoad(img);
      });
    }, {
      rootMargin: '400px 0px',
      threshold: 0.01,
    });

    document.querySelectorAll('img[loading="lazy"]').forEach(observe);
  };

  // подхватываем новые изображения (SPA-навигация и асинхронный рендер блоков)
  const startMutations = () => {
    if (!window.MutationObserver) {
      return;
    }

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!node || node.nodeType !== 1) {
            return;
          }
          if (node.tagName === 'IMG') {
            observe(node);

            return;
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('img[loading="lazy"]').forEach(observe);
          }
        });
      });
    });

    mo.observe(document.body, { childList: true, subtree: true });
  };

  const runPasses = (delays = [400, 1200, 2500, 5000]) => {
    delays.forEach((delay) => setTimeout(() => {
      forceNear(2);
      retryBrokenVisible();
    }, delay));
  };

  const init = () => {
    startObserver();
    startMutations();
    runPasses();

    let timer = null;
    const onScrollOrResize = () => {
      if (timer) {
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        forceNear(1.5);
        retryBrokenVisible();
      }, 250);
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        runPasses([200, 1500]);
      }
    });

    window.addEventListener('nuxt:route-changed', () => runPasses([300, 1200, 2600]));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
